import CheckOutlined from '@mui/icons-material/CheckOutlined'
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined'
import WarningOutlined from '@mui/icons-material/WarningOutlined'
import { ecgTypes, xrayTypes } from './diagnostic_types'
import { ExamType } from '../types'
import { ExamSubtype } from 'types/exam/types'

/**
 * Threshold de decisão do modelo de CXRAY — espelha o `threshold` da tabela de decision brackets
 * do backend (`utils::cxray_predictive_values`). Um `result_value` a partir dele já é predição
 * positiva (traz PPV); abaixo dele é predição negativa (traz NPV).
 */
export const CXRAY_DECISION_THRESHOLD = 0.4

export const CXRAY_SUBTYPES: ExamSubtype[] = [
  'cxray_pa',
  'cxray_ap',
  'cxray_r_lat',
  'cxray_l_lat'
]

export const ECG_SUBTYPES: ExamSubtype[] = [
  'ecg_12_lead',
  'ecg_6_lead',
  'ecg_3_lead',
  'ecg_1_lead'
]

/**
 * Subtipos de raio-x efetivamente usados no filtro da listagem por ora.
 * Os demais subtipos ficam mapeados em CXRAY_SUBTYPES/ECG_SUBTYPES para uso futuro.
 */
export const CXRAY_FILTER_SUBTYPES: ExamSubtype[] = ['cxray_pa', 'cxray_ap']

/**
 * Opções (valor + rótulo) para remarcar o subtype de uma imagem na tela de raio-x.
 * Inclui apenas os subtipos pertinentes a raio-x — `xray_other` cobre incidências
 * fora do tórax (cabeça, pé, etc.). `ecg_other`/`other` não aparecem aqui.
 */
export const CXRAY_SUBTYPE_OPTIONS: { value: ExamSubtype; label: string }[] = [
  { value: 'cxray_pa', label: 'Raio-X PA' },
  { value: 'cxray_ap', label: 'Raio-X AP' },
  { value: 'cxray_l_lat', label: 'Raio-X Lateral Esquerdo' },
  { value: 'cxray_r_lat', label: 'Raio-X Lateral Direito' },
  { value: 'xray_other', label: 'Raio-X (outro)' }
]

export const STATUS_OPTIONS = [
  { label: 'Todos', value: 'all' },
  { label: 'Pendente', value: '0' },
  { label: 'Revisado', value: '1' }
]

/**
 * Opções do filtro de Revisão — só Raio-X. Valor = maior `label_result_value`
 * do exame (ver backend). Mais urgente primeiro.
 */
export const CXRAY_REVIEW_OPTIONS: { value: string; label: string }[] = [
  { value: '2', label: 'Revisão Imediata' },
  { value: '1', label: 'Revisão Prioritária' },
  { value: '0', label: 'Revisão Standard' }
]

/**
 * Opções do filtro de Prioridade — só Raio-X. Valor = maior nível de prioridade
 * (patologia) do exame. Mais grave primeiro.
 */
export const CXRAY_PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: '3', label: 'Crítica' },
  { value: '2', label: 'Alta' },
  { value: '1', label: 'Moderada' },
  { value: '0', label: 'Baixa' }
]

// Os níveis de Revisão/Prioridade do exame são calculados no backend e chegam em
// `review_rank`/`priority_rank` (só Raio-X). O frontend apenas os exibe usando os
// mapas de rótulo/cor abaixo — a fonte de verdade da regra é o
// `sentinela_exam_receiver` (utils::cxray_predictive_values).

/** Rótulo + cor por nível de Revisão do exame (`review_rank`). */
export const REVIEW_RANK_CONFIG: Record<
  0 | 1 | 2,
  { label: string; color: string }
> = {
  2: { label: 'Imediata', color: '#d32f2f' },
  1: { label: 'Prioritária', color: '#f57c00' },
  0: { label: 'Standard', color: '#388e3c' }
}

/** Rótulo + cor por nível de Prioridade do exame (`priority_rank`). */
export const PRIORITY_RANK_CONFIG: Record<
  0 | 1 | 2 | 3,
  { label: string; color: string }
> = {
  3: { label: 'Crítica', color: '#d32f2f' },
  2: { label: 'Alta', color: '#f57c00' },
  1: { label: 'Moderada', color: '#fbc02d' },
  0: { label: 'Baixa', color: '#757575' }
}

/**
 * Patologias (código do modelo) por nível de Prioridade — usado apenas no tooltip
 * explicativo do cabeçalho da coluna. Os ranks em runtime vêm do backend
 * (`review_rank`/`priority_rank`); esta lista espelha o `PRIORITY_BY_TYPE` do
 * `sentinela_exam_receiver` (fonte de verdade) só para exibição.
 */
export const CXRAY_PRIORITY_PATHOLOGIES: Record<0 | 1 | 2 | 3, string[]> = {
  3: ['pneumothorax', 'edema', 'effusion', 'consolidation', 'pneumonia'],
  2: ['mass', 'nodule', 'cardiomegaly'],
  1: ['atelectasis', 'infiltration', 'pleural_thickening'],
  0: ['emphysema', 'fibrosis', 'hernia']
}

export const DIAGNOSTIC_TYPES: Record<
  string,
  Array<{ label: string; value: string }>
> = {
  // ECG Types
  ecg: Object.entries(ecgTypes).map(([key, value]) => ({
    label: value.name,
    value: key
  })),
  // X-Ray Types
  cxray: Object.entries(xrayTypes).map(([key, value]) => ({
    label: value.name,
    value: key
  }))
}

export const EXAM_TYPE_MAP: Record<ExamType, string> = {
  cxray: 'Raio-X',
  ecg: 'ECG'
}

export const resultConfigs = {
  likely: {
    label: 'Provável patologia',
    risk: 'Alto',
    icon: <ErrorOutlineOutlined />,
    color: '#d32f2f'
  },
  possible: {
    label: 'Suspeita de patologia',
    risk: 'Médio',
    icon: <WarningOutlined />,
    color: '#f57c00'
  },
  unlikely: {
    label: 'Patologia não identificada',
    risk: 'Baixo',
    icon: <CheckOutlined />,
    color: '#388e3c'
  }
}

export const resultECGConfigs = {
  likely: {
    label: 'Compatível',
    risk: 'Alto',
    icon: <ErrorOutlineOutlined />,
    color: '#d32f2f'
  },
  possible: {
    label: 'Suspeito',
    risk: 'Médio',
    icon: <WarningOutlined />,
    color: '#f57c00'
  },
  unlikely: {
    label: 'Não identificado',
    risk: 'Baixo',
    icon: <CheckOutlined />,
    color: '#388e3c'
  }
}

export const getResultConfig = (resultValue?: number) => {
  if (resultValue === undefined) return resultConfigs.unlikely

  if (resultValue > 0.8) {
    return resultConfigs.likely
  }

  if (resultValue > 0.6) {
    return resultConfigs.possible
  }

  return resultConfigs.unlikely
}

/**
 * Classificação de resultado de CXRAY a partir do `label_result_value` vindo da API
 * (0 = sem patologia, 1 = suspeita, 2 = provável). Reaproveita as configs existentes.
 */
export const getResultConfigByLabel = (label?: 0 | 1 | 2) => {
  switch (label) {
    case 2:
      return resultConfigs.likely // Provável patologia
    case 1:
      return resultConfigs.possible // Suspeita de patologia
    default:
      return resultConfigs.unlikely // Patologia não identificada
  }
}

/**
 * Formata um valor preditivo (0–1) como percentual pt-BR com 1 casa decimal.
 * Retorna `null` quando o valor não está disponível.
 */
export const formatPredictiveValue = (value?: number): string | null =>
  value == null
    ? null
    : `${(value * 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}%`

export const getECGResultConfig = (resultValue?: number) => {
  if (resultValue === undefined) return resultECGConfigs.unlikely

  if (resultValue > 0.8) {
    return resultECGConfigs.likely
  }

  if (resultValue > 0.6) {
    return resultECGConfigs.possible
  }

  return resultECGConfigs.unlikely
}
